/**
 * The sources table's fixed chrome: the card and the column headers. Only the
 * rows differ between the resolved table and its skeleton, so only the rows are
 * a prop.
 *
 * Eager, and deliberately not part of `log-sources-table.tsx`: that component is
 * `lazy()`, and the skeleton renders this chrome for real (P-05 §1) while the
 * chunk is still in flight. Owning the chrome in one place is also what stops the
 * loading and resolved tables from drifting a column apart.
 */
import { cn } from '@/utils/ui/styles'

import { Card } from '@/components/ui/card'
import {
	Table,
	TableBody,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'

import type { ReactNode } from 'react'

/** In render order. `className` carries the first column's extra left padding. */
const COLUMNS: ReadonlyArray<{ label: string; className?: string }> = [
	{ label: 'Source', className: 'px-4' },
	{ label: 'Port' },
	{ label: 'Status' },
	{ label: 'Last log' },
	{ label: 'Volume · 24h' },
]

const HEAD_CLASS = 'text-muted-foreground text-xs tracking-wide uppercase'

type LogSourceTableFrameProps = {
	/** `<TableRow>`s — real ones, or the skeleton's placeholders. */
	children: ReactNode
}

export const LogSourceTableFrame = ({ children }: LogSourceTableFrameProps) => {
	return (
		<Card className="gap-0 overflow-hidden p-0">
			<Table>
				<TableHeader>
					<TableRow>
						{COLUMNS.map((column) => (
							<TableHead
								key={column.label}
								className={cn(HEAD_CLASS, column.className)}
							>
								{column.label}
							</TableHead>
						))}
						{/* Unlabelled: the column holds one button whose own text says what
								it does, and an `ACTIONS` header would be a word describing the
								table rather than the data. */}
						<TableHead className="w-0" />
					</TableRow>
				</TableHeader>

				<TableBody>{children}</TableBody>
			</Table>
		</Card>
	)
}
